import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Plus, X, Save, Tag } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/helpers";

const FIELD_META = [
  { key: "gender", label: "Gender" },
  { key: "blood_group", label: "Blood Group" },
  { key: "category", label: "Category" },
  { key: "admission_type", label: "Admission Type" },
  { key: "tc_status", label: "TC Status" },
  { key: "religion", label: "Religion" },
  { key: "caste", label: "Caste" },
  { key: "nationality", label: "Nationality" },
  { key: "house", label: "House" },
];

export default function StudentSettingsTab() {
  const { user } = useAuth();
  const canSave = hasPerm(user, "manage_settings");
  const [draft, setDraft] = useState(null);
  const [newVal, setNewVal] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/student-field-settings")
      .then((r) => setDraft(r.data.fields || {}))
      .catch(() => toast.error("Failed to load student settings"));
  }, []);

  const add = (key) => {
    const v = (newVal[key] || "").trim();
    if (!v) return;
    setDraft((p) => {
      const cur = p[key] || [];
      if (cur.some((x) => x.toLowerCase() === v.toLowerCase())) return p;
      return { ...p, [key]: [...cur, v] };
    });
    setNewVal((p) => ({ ...p, [key]: "" }));
  };
  const remove = (key, v) => setDraft((p) => ({ ...p, [key]: (p[key] || []).filter((x) => x !== v) }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/student-field-settings", { fields: draft });
      setDraft(r.data.fields || {});
      toast.success("Student field options saved");
    } catch { toast.error("Failed to save student settings"); }
    finally { setSaving(false); }
  };

  if (!draft) return <div className="py-16 flex justify-center text-slate-400"><Loader2 className="animate-spin mr-2" />Loading…</div>;

  return (
    <div className="space-y-6" data-testid="student-settings-tab">
      <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 text-sm text-indigo-800" data-testid="student-settings-note">
        These option lists power the dropdowns on the <b>Add / Edit Student</b> page.
        <b> Class</b> &amp; <b>Section</b> are managed in the <b>Classes</b> module, <b>Stream</b> under New Session / Class settings, and student <b>Tags</b> via the Tag picker on the student form.
      </div>
      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {FIELD_META.map((fld) => (
          <div key={fld.key} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`student-field-${fld.key}`}>
            <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-3"><Tag size={16} className="text-indigo-500" />{fld.label}</h3>
            <div className="flex flex-wrap gap-2 mb-3 min-h-[28px]">
              {(draft[fld.key] || []).length === 0 && <span className="text-sm text-slate-400">No options yet.</span>}
              {(draft[fld.key] || []).map((v) => (
                <span key={v} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 rounded-full pl-3 pr-1 py-1 text-sm" data-testid={`${fld.key}-chip-${v}`}>
                  {v}
                  {canSave && <button onClick={() => remove(fld.key, v)} className="p-0.5 rounded-full hover:bg-slate-200 text-slate-400 hover:text-rose-500" data-testid={`${fld.key}-remove-${v}`}><X size={14} /></button>}
                </span>
              ))}
            </div>
            {canSave && (
              <div className="flex gap-2">
                <Input value={newVal[fld.key] || ""} onChange={(e) => setNewVal((p) => ({ ...p, [fld.key]: e.target.value }))} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(fld.key); } }} placeholder={`New ${fld.label.toLowerCase()}`} data-testid={`${fld.key}-new-input`} />
                <Button variant="outline" onClick={() => add(fld.key)} data-testid={`${fld.key}-add-btn`}><Plus size={15} /></Button>
              </div>
            )}
          </div>
        ))}
      </div>
      {canSave ? (
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="save-student-settings-btn">{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Save Settings</Button>
        </div>
      ) : (
        <div className="text-sm text-slate-400">You don't have permission to edit these settings.</div>
      )}
    </div>
  );
}
